import React, { useEffect } from 'react';
import {
    FileSignature, ShieldCheck, CreditCard, ClipboardList, Video, LayoutGrid,
    Clock, CheckCircle2, Zap, MoveRight, Download, ExternalLink, FolderOpen,
} from 'lucide-react';

const SLUG = 'shantanu';
const NOTION_CONTENT_OS = 'https://www.notion.so/3e63ac2a53098149a3b2d34e08a6e960';

const QUESTIONNAIRE_URL = 'https://docs.google.com/document/d/1FSYwqN5_s8Uvpji-fMOSTu8VZ0A5EmT1of43WuUxaB0/edit';
const DRIVE_EXAMPLE_URL = 'https://drive.google.com/drive/folders/1sh4_G0gAFi9UO2rcarQk1JcNCrfkt8L9?usp=sharing';

const data = {
    client: 'Shantanu',
    brandLabel: 'Shantanu Pandey',
    pageTitle: 'Onboarding | Shantanu Pandey via SocialSEO',
    badge: 'Ready to Build',
    headline: 'Welcome to the Machine.',

    greeting: 'Hey Shantanu,',
    intro: [
        'Good to have you in. This page is the single home base for getting your personal page engine started. Everything you need to do in the first week is laid out below as a short checklist, and most of it is sending us links to things you already have.',
        'How the engagement runs: the first 7 days are the build. We map your positioning from the documentation you already own, train your clone from the recordings below, load your B-roll library, and deploy the content engine. Day 8 is the first post.',
        'The clone is built on your consent, used only for your content during the engagement, and stops on exit. That was your condition on the calls, and it goes into the agreement as a clause.',
    ],

    steps: [
        {
            icon: FileSignature,
            title: 'Review and sign your agreement',
            desc: 'Please read it properly so we are fully aligned on deliverables, on ownership of the page and the assets, and on the clone consent clause. We need it signed before the build starts.',
            links: [
                { label: 'Service Agreement (SLA)', href: `/assets/${SLUG}/agreement.pdf`, icon: Download },
            ],
        },
        {
            icon: CreditCard,
            title: 'Clear your first invoice',
            desc: 'Invoiced in INR, payable by bank transfer. Our full account details are printed on the invoice itself. If the transfer goes out from the UAE, set it to the "OUR" charge code so the full amount reaches us.',
            links: [
                { label: 'Download Invoice (bank details inside)', href: `/assets/${SLUG}/invoice_PENDING.pdf`, icon: Download },
            ],
        },
        {
            icon: FolderOpen,
            title: 'Send us what already exists',
            desc: 'You have done more prep than most founders. Put these in one Drive folder, set sharing to "Anyone with the link can view", and send us the link. We map your positioning from these documents.',
            tips: [
                'The documentation pack from your previous agency: ICP psychology, archetype, backstory, parables, the three pillars and every script written under them.',
                'Your B-roll library, the UAE and London shoots. Nothing new to shoot.',
                'The handle of the page we will run, and how you want to hand us posting access. The page stays in your name from day one.',
            ],
            links: [],
        },
        {
            icon: ClipboardList,
            title: 'Founder Questionnaire, only if needed',
            desc: 'Send the previous-agency documents first. We read them against what we usually ask a founder. If they cover it, you skip this step entirely. If they leave gaps, we tell you which questions, and you answer only those in your own copy below.',
            links: [
                { label: 'Open your Questionnaire', href: QUESTIONNAIRE_URL, icon: ExternalLink },
            ],
        },
        {
            icon: Video,
            title: 'Record the clone training data',
            desc: 'This is what your clone is built from, and it is the one thing only you can do. Put the recordings in one Drive folder organized like the example below, set it to "Anyone with the link can view", and send us the link. Use whatever you have: a phone is enough, a mic if you own one, and if you already have lights and a setup from your B-roll shoots, record there. All of this can be done while travelling.',
            links: [
                { label: 'See the Example Folder', href: DRIVE_EXAMPLE_URL, icon: ExternalLink },
            ],
            tips: [
                'Audio, up to 2 hours total: your voice only, in a quiet room, on a phone voice memo or a mic if you have one. Read a book or a document page you have, or talk in flow about something you are genuinely passionate about. Split it into files of 10 to 20 minutes.',
                'Video, at least 25 minutes: five clips of five minutes, each in a different outfit. Phone in portrait or your existing setup, camera at eye level, facing the light. Head and shoulders in frame, the top of your head never cut off.',
                'Full eye contact with the lens the whole time you speak. Talk in flow about what you know, no script to remember or edit. Speak in complete sentences, and if you stutter, keep going. Natural beats polished.',
                'Start and end every clip with five seconds of silence, face relaxed. No hats, no sunglasses, no hands on the face, plain background.',
                'Portraits: ten to fifteen clean stills in the same light, front and three-quarter angles, plus three full-length. These become thumbnails and covers.',
                'Your voice and face data are used only to produce your content during this engagement and are deleted on exit. Nobody else is ever built from them.',
            ],
        },
        {
            icon: LayoutGrid,
            title: 'Open your Content OS',
            desc: 'Your Notion workspace is where the whole engine lives: the angle bank, the scripts pipeline, the posting calendar, and approvals. This is where you give inputs and sign off on content.',
            links: [
                { label: 'Open your Content OS', href: NOTION_CONTENT_OS, icon: ExternalLink },
            ],
        },
    ],

    timelineTitle: 'What the engagement looks like',
    phases: [
        {
            title: 'The Build',
            window: 'Days 1–7',
            items: [
                'Agreement signed, first invoice cleared, documents and recordings in.',
                'Positioning mapped from your previous-agency pack. Angle bank and the first month of scripts drafted for your approval.',
                'Clone trained and pinned, B-roll library loaded, posting calendar set.',
            ],
        },
        {
            title: 'First Post',
            window: 'Day 8',
            items: [
                'First video live on your page.',
                'Comment tracking starts from post one.',
            ],
        },
        {
            title: 'Month 1',
            window: 'Weeks 2–5',
            items: [
                '28 originals across the month, roughly one a day.',
                'Weekly report on who is commenting and what it says about the audience we are pulling in.',
            ],
        },
        {
            title: 'Compound',
            window: 'Months 2–3',
            items: [
                'The formats and hooks that pull the right commenters get more volume.',
                'Month-3 checkpoint against the written month-1 baseline.',
                'Monthly direction call with you on numbers and what comes next.',
            ],
        },
    ],
    timelineNote:
        'Week one is the heavy lift on your side: documents, recordings, sign-off. After that your only standing job is approvals inside the Content OS and the monthly call.',

    closing: {
        line1: 'Any questions on the recording setup or the documents, message me on WhatsApp.',
        line2: 'Send the recordings while you travel and we build while you fly.',
    },
};

export default function ShantanuOnboarding() {
    useEffect(() => {
        document.title = data.pageTitle;
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-24 relative z-10 min-h-screen">
            {/* Background Gradient */}
            <div className="fixed inset-0 min-h-screen bg-[#080B11] -z-20 overflow-hidden">
                <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-indigo-600/20 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
                <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-emerald-600/15 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay pointer-events-none" />
            </div>

            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-6 flex justify-between items-center bg-[#07090E]/80 backdrop-blur-lg border-b border-white/5">
                <div className="flex items-center gap-3 w-full max-w-5xl mx-auto">
                    <span className="font-bold tracking-tight text-white text-xl">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/20 mx-2">/</span>
                    <span className="text-white/80 font-medium">{data.brandLabel} Onboarding</span>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 pt-10 space-y-16">

                {/* Intro */}
                <section>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-bold tracking-wide uppercase mb-6">
                        <CheckCircle2 className="w-4 h-4" /> {data.badge}
                    </div>

                    <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-tight">
                        {data.headline}
                    </h1>

                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 space-y-4 text-white/80 text-lg leading-relaxed shadow-xl">
                        <p><strong>{data.greeting}</strong></p>
                        {data.intro.map((para, i) => <p key={i}>{para}</p>)}
                    </div>
                </section>

                {/* Onboarding checklist */}
                <section>
                    <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
                        <CheckCircle2 className="text-emerald-400 w-6 h-6" /> Your First Steps
                    </h2>

                    <div className="space-y-5">
                        {data.steps
                            .filter((step) => !step.links.some((l) => l.href.includes('_PENDING')))
                            .map((step, i) => {
                                const StepIcon = step.icon;
                                return (
                                    <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-7 shadow-xl hover:border-indigo-500/40 transition-colors">
                                        <div className="flex items-start gap-4">
                                            <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center font-bold relative ${step.done ? 'bg-emerald-500/15 text-emerald-400' : 'bg-indigo-500/15 text-indigo-400'}`}>
                                                <StepIcon className="w-5 h-5" />
                                                <span className={`absolute -top-2 -right-2 w-5 h-5 rounded-full text-white text-[11px] font-bold flex items-center justify-center border-2 border-[#0B0E14] ${step.done ? 'bg-emerald-500' : 'bg-indigo-500'}`}>{step.done ? '✓' : i + 1}</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-xl font-bold text-white mb-1 flex items-center gap-2 flex-wrap">{step.title}{step.done && <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">Done</span>}</h3>
                                                <p className="text-slate-400 text-sm leading-relaxed mb-4">{step.desc}</p>

                                                {step.tips && (
                                                    <ul className="mb-4 space-y-2">
                                                        {step.tips.map((tip, t) => (
                                                            <li key={t} className="flex gap-2.5 text-slate-300 text-sm">
                                                                <Zap className="w-4 h-4 text-indigo-400 shrink-0 mt-0.5" />
                                                                <span>{tip}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}

                                                <div className="flex flex-wrap gap-3">
                                                    {step.links.map((link, l) => {
                                                        const LinkIcon = link.icon || ExternalLink;
                                                        return (
                                                            <a
                                                                key={l}
                                                                href={link.href}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-500/10 border border-indigo-500/25 text-indigo-300 font-semibold text-sm hover:bg-indigo-500/20 hover:border-indigo-400 hover:text-white transition-colors"
                                                            >
                                                                <LinkIcon className="w-4 h-4" /> {link.label} <MoveRight className="w-4 h-4" />
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </section>

                {/* Timeline */}
                <section>
                    <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
                        <Clock className="text-indigo-400 w-6 h-6" /> {data.timelineTitle}
                    </h2>

                    <div className="relative border-l border-white/10 ml-4 md:ml-6 space-y-10 pb-4">
                        {data.phases.map((phase, i) => (
                            <div key={i} className="relative pl-8 md:pl-10">
                                <div className={`absolute -left-[14px] top-1 w-7 h-7 rounded-full flex items-center justify-center ${
                                    i === 0
                                        ? 'bg-indigo-500 border-[4px] border-[#07090E] shadow-[0_0_15px_rgba(99,102,241,0.5)]'
                                        : 'bg-[#1A1D24] border border-white/20'
                                }`}>
                                    <span className={`text-xs font-bold ${i === 0 ? 'text-white' : 'text-white/60'}`}>{i + 1}</span>
                                </div>
                                <h3 className="text-xl font-bold mb-1 text-white">{phase.title}</h3>
                                <p className={`text-sm font-semibold mb-3 uppercase tracking-wider ${i === 0 ? 'text-indigo-400' : 'text-indigo-400/70'}`}>
                                    {phase.window}
                                </p>
                                <ul className={`space-y-3 ${i === 0 ? 'text-slate-300' : 'text-slate-300/80'}`}>
                                    {phase.items.map((item, j) => (
                                        <li key={j} className="flex gap-3">
                                            <Zap className="w-5 h-5 text-indigo-400 shrink-0" />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    {data.timelineNote && (
                        <div className="bg-indigo-500/10 border border-indigo-500/20 p-5 rounded-xl text-indigo-200 text-sm leading-relaxed mt-6">
                            <strong className="text-indigo-400">Note:</strong> {data.timelineNote}
                        </div>
                    )}
                </section>

                {/* Closing */}
                <section>
                    <div className="bg-gradient-to-br from-slate-900 to-[#0F172A] border border-white/10 rounded-3xl p-8 shadow-2xl relative overflow-hidden text-center">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 blur-[80px] rounded-full pointer-events-none" />
                        <ShieldCheck className="w-9 h-9 text-emerald-400 mx-auto mb-4" />
                        <p className="text-white/60 mb-2">{data.closing.line1}</p>
                        <p className="text-lg font-bold text-white">{data.closing.line2}</p>
                    </div>
                </section>

            </main>
        </div>
    );
}
